import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ClientUserDto, RegisterClientUserRequest } from "@evolys/types";
import { ApiClient, PortalApi } from "@evolys/api-client";

export interface PortalAuthContextValue {
  user: ClientUserDto | null;
  canOrder: boolean;
  canViewResults: boolean;
  canDownloadPdfs: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: RegisterClientUserRequest) => Promise<ClientUserDto>;
}

const PortalAuthContext = createContext<PortalAuthContextValue | undefined>(
  undefined,
);

const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes for portal
const WARNING_BEFORE_MS = 5 * 60 * 1000; // warn at 25 minutes

interface PortalAuthProviderProps {
  children: ReactNode;
  apiBaseUrl: string;
  skipSessionCheck?: boolean;
  onSessionExpired?: () => void;
  onInactivityWarning?: () => void;
}

export function PortalAuthProvider({
  children,
  apiBaseUrl,
  skipSessionCheck = false,
  onSessionExpired,
  onInactivityWarning,
}: PortalAuthProviderProps) {
  const [user, setUser] = useState<ClientUserDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const portalApi = useRef(new PortalApi(new ApiClient(apiBaseUrl))).current;
  const inactivityTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
    if (warningTimer.current) clearTimeout(warningTimer.current);
  }, []);

  const handleSessionExpired = useCallback(() => {
    setUser(null);
    clearTimers();
    onSessionExpired?.();
  }, [clearTimers, onSessionExpired]);

  const resetInactivityTimer = useCallback(() => {
    clearTimers();
    if (!user) return;

    warningTimer.current = setTimeout(() => {
      onInactivityWarning?.();
    }, INACTIVITY_TIMEOUT_MS - WARNING_BEFORE_MS);

    inactivityTimer.current = setTimeout(async () => {
      try {
        await portalApi.logout();
      } catch {
        // ignore — session may already be expired
      }
      handleSessionExpired();
    }, INACTIVITY_TIMEOUT_MS);
  }, [user, clearTimers, portalApi, handleSessionExpired, onInactivityWarning]);

  // Track user activity for inactivity timeout
  useEffect(() => {
    if (!user) return;

    const events = ["mousedown", "keydown", "scroll", "touchstart"] as const;
    const handler = () => resetInactivityTimer();

    for (const event of events) {
      window.addEventListener(event, handler, { passive: true });
    }
    resetInactivityTimer();

    return () => {
      for (const event of events) {
        window.removeEventListener(event, handler);
      }
      clearTimers();
    };
  }, [user, resetInactivityTimer, clearTimers]);

  // Check session on mount
  useEffect(() => {
    if (skipSessionCheck) {
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function checkSession() {
      try {
        const result = await portalApi.me();
        if (!cancelled) {
          setUser(result.user);
        }
      } catch {
        // Not authenticated — that's fine
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    checkSession();
    return () => {
      cancelled = true;
    };
  }, [portalApi, skipSessionCheck]);

  const login = useCallback(
    async (email: string, password: string) => {
      const result = await portalApi.login({ email, password });
      setUser(result.user);
    },
    [portalApi],
  );

  const logout = useCallback(async () => {
    try {
      await portalApi.logout();
    } catch {
      // ignore
    }
    setUser(null);
    clearTimers();
  }, [portalApi, clearTimers]);

  const register = useCallback(
    async (data: RegisterClientUserRequest): Promise<ClientUserDto> => {
      return portalApi.register(data);
    },
    [portalApi],
  );

  const value: PortalAuthContextValue = {
    user,
    canOrder: user?.canOrder ?? false,
    canViewResults: user?.canViewResults ?? false,
    canDownloadPdfs: user?.canDownloadPdfs ?? false,
    isAuthenticated: user !== null,
    isLoading,
    login,
    logout,
    register,
  };

  return (
    <PortalAuthContext.Provider value={value}>
      {children}
    </PortalAuthContext.Provider>
  );
}

export function usePortalAuth(): PortalAuthContextValue {
  const context = useContext(PortalAuthContext);
  if (!context) {
    throw new Error("usePortalAuth must be used within a PortalAuthProvider");
  }
  return context;
}
